import logoSchoolPlaceholder from '@/assets/placeholders/logo-school-placeholder.webp'
import { hexToHSL } from '@/helpers'
import z from 'zod'

export class SchoolDefaults {
  public static readonly PrimaryColor = '#990000'
  public static readonly SecondaryColor = '#ffcccc'
  public static readonly Logo = logoSchoolPlaceholder

  public static DefaultColorStyles() {
    const primaryHsl = hexToHSL(SchoolDefaults.PrimaryColor)
    const secondaryHsl = hexToHSL(SchoolDefaults.SecondaryColor)

    return {
      '--color-primary': SchoolDefaults.PrimaryColor,
      '--color-secondary': SchoolDefaults.SecondaryColor,
      '--color-primary-h': primaryHsl?.h,
      '--color-primary-s': primaryHsl?.s + '%',
      '--color-primary-l': primaryHsl?.l + '%',
      '--color-secondary-h': secondaryHsl?.h,
      '--color-secondary-s': secondaryHsl?.s + '%',
      '--color-secondary-l': secondaryHsl?.l + '%'
    }
  }

  public static DefaultCalendarColorStyles() {
    return {
      '--vc-accent-500': SchoolDefaults.PrimaryColor,
      '--vc-accent-800': SchoolDefaults.SecondaryColor
    }
  }
}

const GoogleCalendarResourceSchema = z.object({
  id: z.string(),
  iCSLinkUrl: z.string().optional()
})

export class GoogleCalendarResource {
  constructor(data: z.infer<typeof GoogleCalendarResourceSchema>) {
    Object.assign(this, data)
  }
  public readonly id!: string
  public readonly iCSLinkUrl?: string
}

const AppConfigurationSchema = z.object({
  primaryColor: z.string().optional(),
  primaryContrastColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  secondaryContrastColor: z.string().optional(),
  tertiaryColor: z.string().optional(),
  tertiaryContrastColor: z.string().optional(),
  logoUrl: z.string().optional(),
  smallLogoUrl: z.string().optional(),
  logoIncludesTitle: z.boolean(),
  googleCalendarResourceList: z.array(GoogleCalendarResourceSchema).optional()
})

export class AppConfiguration {
  constructor(data: z.infer<typeof AppConfigurationSchema>) {
    Object.assign(this, data)
    this.googleCalendarResourceList = data.googleCalendarResourceList?.map(
      resource => new GoogleCalendarResource(resource)
    )
  }
  public readonly primaryColor?: string
  public readonly primaryContrastColor?: string
  public readonly secondaryColor?: string
  public readonly secondaryContrastColor?: string
  public readonly tertiaryColor?: string
  public readonly tertiaryContrastColor?: string
  public readonly logoUrl?: string
  public readonly smallLogoUrl?: string
  public readonly logoIncludesTitle!: boolean
  public readonly googleCalendarResourceList?: GoogleCalendarResource[]
}

const SchoolResourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  fileName: z.string(),
  iconAllowColor: z.boolean(),
  iconColor: z.string().optional(),
  iconUrl: z.string().optional(),
  rawIconUrl: z.string().optional(),
  resourceAlt: z.string().optional(),
  resourceTarget: z.string().optional(),
  resourceUrl: z.string().optional(),
})

export class SchoolResource {
  constructor(data: z.infer<typeof SchoolResourceSchema>) {
    Object.assign(this, data)
  }
  public readonly id!: string
  public readonly name!: string
  public readonly fileName!: string
  public readonly iconAllowColor!: boolean
  public readonly iconColor?: string
  public readonly iconUrl?: string
  public readonly rawIconUrl?: string
  public readonly resourceAlt?: string
  public readonly resourceTarget?: string
  public readonly resourceUrl?: string
}

const CMSDataSchema = z.object({
  id: z.number(),
  appConfiguration: AppConfigurationSchema,
  schoolResources: z.array(SchoolResourceSchema)
})

export class CMSData {
  constructor(data: z.infer<typeof CMSDataSchema>) {
    this.id = data.id
    this.appConfiguration = new AppConfiguration(data.appConfiguration)
    this.schoolResources = data.schoolResources.map(resource => new SchoolResource(resource))
  }
  public readonly id!: number
  public readonly appConfiguration!: AppConfiguration
  public readonly schoolResources!: SchoolResource[]
}

const SchoolSchema = z.object({
  schoolId: z.number(),
  districtId: z.number(),
  orgId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  siteId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  feedId: z.number().transform(i => i === 0 ? undefined : i).optional(),
  name: z.string(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  cmsData: CMSDataSchema.optional()
})

export class School {
  constructor(data: z.infer<typeof SchoolSchema>) {
    Object.assign(this, data)
    this.cmsData = data.cmsData ? new CMSData(data.cmsData) : undefined
  }

  public readonly schoolId!: number
  public readonly districtId!: number
  public readonly orgId?: number
  public readonly siteId?: number
  public readonly feedId?: number
  public readonly name!: string
  public readonly address!: string
  public readonly city!: string
  public readonly state!: string
  public readonly zip!: string
  public readonly phone?: string
  public readonly latitude?: number
  public readonly longitude?: number
  public readonly cmsData?: CMSData

  isAssociatedWith(item: object) {
    return 'schoolId' in item && typeof item.schoolId === 'number' && item.schoolId == this.schoolId
  }

  primaryColor() {
    return this.cmsData?.appConfiguration.primaryColor ?? SchoolDefaults.PrimaryColor
  }

  secondaryColor() {
    return this.cmsData?.appConfiguration.secondaryColor ?? SchoolDefaults.SecondaryColor
  }

  primaryColorHsl() {
    return hexToHSL(this.cmsData?.appConfiguration.primaryColor ?? SchoolDefaults.PrimaryColor)
  }

  secondaryColorHsl() {
    return hexToHSL(this.cmsData?.appConfiguration.secondaryColor ?? SchoolDefaults.SecondaryColor)
  }

  logo() {
    return this.cmsData?.appConfiguration.logoUrl
  }

  logoOrDefault() {
    return this.logo() ?? SchoolDefaults.Logo
  }

  resources() {
    return this.cmsData?.schoolResources ?? []
  }

  calendarResources() {
    return this.cmsData?.appConfiguration.googleCalendarResourceList ?? []
  }

  hasAnnouncementsIntegration() {
    return !!this.orgId && !!this.siteId && !!this.feedId
  }

  getColorStyles() {
    const primary = this.primaryColor()
    const secondary = this.secondaryColor()
    const primaryHsl = this.primaryColorHsl()
    const secondaryHsl = this.secondaryColorHsl()

    return {
      '--color-primary': primary,
      '--color-secondary': secondary,
      '--color-primary-h': primaryHsl?.h,
      '--color-primary-s': primaryHsl?.s + '%',
      '--color-primary-l': primaryHsl?.l + '%',
      '--color-secondary-h': secondaryHsl?.h,
      '--color-secondary-s': secondaryHsl?.s + '%',
      '--color-secondary-l': secondaryHsl?.l + '%'
    }
  }

  getCalendarColorStyles() {
    const primary = this.primaryColor()
    const secondary = this.secondaryColor()
    return {
      '--vc-accent-500': primary,
      '--vc-accent-800': secondary
    }
  }
}

export class Schools {
  public static GetAssociatedSchool(schools: School[], item: object) {
    return schools.find(s => s.isAssociatedWith(item))
  }

  public static Resources(schools: School[]) {
    return schools.flatMap(s  => s.resources())
  }
}

export const schoolSchemas = {
  AppConfiguration: AppConfigurationSchema,
  CMSData: CMSDataSchema,
  GoogleCalendarResource: GoogleCalendarResourceSchema,
  SchoolResource: SchoolResourceSchema,
  School: SchoolSchema
}
